import {Trans} from '@lingui/macro';
import React, {FC, useCallback} from 'react';
import styled from 'styled-components';

import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {UnreachableError} from '../../../../../libs/shared/helpers/src/types/customErrors';
import {collectEnumValues} from '../../../../../libs/shared/helpers/src/typescript/enumHelpers';
import {DropdownBoxCoordinator} from '../../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {DropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItem';
import {DropdownItemContent} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {SimpleDropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverTransferProps} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {getServerAuthenticationStrategyLabel} from '../../../../models/api/serverHelpers';
import {
  ServerAuthenticationConfig,
  ServerAuthenticationStrategiesEnum,
  ServerSendAsTargetsEnum,
} from '../../../../models/api/serverModel';
import {ServerEditorStateActions} from '../../../editors/serverEditor/useServerEditorState';
import {
  StyledParameterLineDiv,
  StyledParameterTitleLineDiv,
} from '../../../rules/ruleEditor/expressions/parameterLine';
import {RuleMultilineParameterCard} from '../../../rules/ruleEditor/ruleMultilineParameterCard';
import {ServerAuthenticationBearerInputs} from '../inputs/serverAuthenticationBearerInputs';
import {ServerAuthenticationOAuth2Inputs} from '../inputs/serverAuthenticationOAuth2Inputs';

/*
 * Constants.
 */

const ServerAuthenticationStrategiesEnumValues = collectEnumValues(ServerAuthenticationStrategiesEnum);

/*
 * Props.
 */

interface ServerAuthenticationConfigCardProps {
  authenticationConfig: ServerAuthenticationConfig;
  onUpdate: ServerEditorStateActions['onUpdateAuthenticationConfig'];
  isReadOnly: boolean;
}

/*
 * Style.
 */

const StyledDropdownBoxCoordinator = styled(DropdownBoxCoordinator)`
  padding: 0px;
  margin-bottom: 20px;
`;

/*
 * Component.
 */

export const ServerAuthenticationConfigCard: FC<ServerAuthenticationConfigCardProps> = (props) => {
  const {authenticationConfig, onUpdate, isReadOnly} = props;

  const onUpdateStrategy = useCallback(
    (strategy: ServerAuthenticationStrategiesEnum) => {
      switch (strategy) {
        case ServerAuthenticationStrategiesEnum.BEARER:
          return onUpdate({
            strategy,
            sendAs: ServerSendAsTargetsEnum.HEADER,
          });

        case ServerAuthenticationStrategiesEnum.OAUTH2:
          return onUpdate({
            strategy,
            clientId: '',
            clientSecret: '',
            scope: '',
            tokenUrl: '',
            authorizationUrl: '',
          });

        case ServerAuthenticationStrategiesEnum.BASIC:
        case ServerAuthenticationStrategiesEnum.NONE:
          return onUpdate({strategy});

        default:
          throw new UnreachableError(strategy);
      }
    },
    [onUpdate],
  );

  return (
    <RuleMultilineParameterCard
      title={<Trans id="server-editor-authentication-title">Authentication</Trans>}
      icon={<Icon name="lockFilled" />}
    >
      <StyledParameterLineDiv>
        <StyledParameterTitleLineDiv>
          <Trans id="server-editor-authentication-strategy">Strategy</Trans>
        </StyledParameterTitleLineDiv>
        <StyledDropdownBoxCoordinator
          size={VisualSizesEnum.MEDIUM}
          value={renderValue(authenticationConfig.strategy)}
          renderDropdown={(popoverProps) =>
            renderDropdown(popoverProps, authenticationConfig.strategy, onUpdateStrategy)
          }
          isDisabled={isReadOnly}
        />
      </StyledParameterLineDiv>

      {maybeRenderAuthenticationInputs(authenticationConfig, onUpdate, isReadOnly)}
    </RuleMultilineParameterCard>
  );
};

/*
 * Helpers.
 */

function renderValue(strategy: ServerAuthenticationStrategiesEnum) {
  return getServerAuthenticationStrategyLabel(strategy);
}

function renderDropdown(
  popoverProps: PopoverTransferProps,
  selectedStrategy: ServerAuthenticationStrategiesEnum,
  onSelect: HandlerOf<ServerAuthenticationStrategiesEnum>,
) {
  return (
    <SimpleDropdown {...popoverProps}>
      {ServerAuthenticationStrategiesEnumValues.map((strategy) =>
        renderDropdownItem(strategy, strategy === selectedStrategy, onSelect),
      )}
    </SimpleDropdown>
  );
}

function renderDropdownItem(
  strategy: ServerAuthenticationStrategiesEnum,
  isChecked: boolean,
  onSelect: HandlerOf<ServerAuthenticationStrategiesEnum>,
) {
  return (
    <DropdownItem
      key={strategy}
      isChecked={isChecked}
      onSelect={() => onSelect(strategy)}
      shouldReserveRightIcon
    >
      <DropdownItemContent isChecked={isChecked}>
        {getServerAuthenticationStrategyLabel(strategy)}
      </DropdownItemContent>
    </DropdownItem>
  );
}

function maybeRenderAuthenticationInputs(
  authenticationConfig: ServerAuthenticationConfig,
  onUpdate: ServerEditorStateActions['onUpdateAuthenticationConfig'],
  isReadOnly: boolean,
) {
  switch (authenticationConfig.strategy) {
    case ServerAuthenticationStrategiesEnum.BEARER:
      return (
        <ServerAuthenticationBearerInputs
          sendAs={authenticationConfig.sendAs}
          onUpdate={onUpdate}
          isReadOnly={isReadOnly}
        />
      );

    case ServerAuthenticationStrategiesEnum.OAUTH2:
      return (
        <ServerAuthenticationOAuth2Inputs
          clientId={authenticationConfig.clientId}
          clientSecret={authenticationConfig.clientSecret}
          scope={authenticationConfig.scope}
          tokenUrl={authenticationConfig.tokenUrl}
          authorizationUrl={authenticationConfig.authorizationUrl}
          onUpdate={onUpdate}
          isReadOnly={isReadOnly}
        />
      );

    case ServerAuthenticationStrategiesEnum.BASIC:
    case ServerAuthenticationStrategiesEnum.NONE:
      return null;

    default:
      throw new UnreachableError(authenticationConfig);
  }
}
